import React from 'react';

import type { FeaturedProduct } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProductCard';
import { FeaturedProductTray } from 'src/modules/renderings/components/RenderingsHub/FeaturedProduct/FeaturedProductTray';
import { PAINTABLE_MATERIALS } from 'src/modules/renderings/constants/labels';
import { useRenderingContext } from 'src/modules/renderings/hooks';
import type { ImageLike } from 'src/modules/renderings/imageLike';
import type { Rendering } from 'src/modules/renderings/types';
import type { GetProductVariationsQuery } from 'src/shared/graphql/generated';
import {
  ProductCatalogFacetTypeEnum,
  useGetProductVariationsQuery,
} from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

type FeaturedProductsProps = {
  img: ImageLike;
};

const findRenderingFile = (renderings: Rendering[], img: ImageLike) => {
  for (
    let renderingIdx = 0;
    renderingIdx < renderings.length;
    renderingIdx += 1
  ) {
    const rendering = renderings[renderingIdx];
    for (
      let versionIdx = 0;
      versionIdx < rendering.versions.length;
      versionIdx += 1
    ) {
      const version = rendering.versions[versionIdx];
      for (
        let fileIdx = 0;
        fileIdx < version.hiDefRenderingFiles.length;
        fileIdx += 1
      ) {
        const file = version.hiDefRenderingFiles[fileIdx];
        if (file.image === img) {
          return file;
        }
      }
    }
  }

  return undefined;
};

const convertProductCatalogVariationsToFeaturedProducts = (
  data: GetProductVariationsQuery,
): FeaturedProduct[] => {
  const featuredProducts = [];
  const variationNodes = data.productCatalogVariations.nodes ?? [];

  for (let i = 0; i < variationNodes.length; i += 1) {
    const featuredProductNode = variationNodes[i];
    if (featuredProductNode) {
      const productSize = (featuredProductNode.variationFacets || []).reduce(
        (previousValue, facet) => {
          if (facet.facetType === ProductCatalogFacetTypeEnum.Sizing) {
            return facet.value;
          }
          return previousValue;
        },
        '',
      );

      const productComposition = (
        featuredProductNode.variationFacets || []
      ).reduce((previousValue, facet) => {
        if (facet.facetType === ProductCatalogFacetTypeEnum.Composition) {
          return facet.value;
        }
        return previousValue;
      }, '');

      featuredProducts.push({
        id: featuredProductNode.id,
        category: featuredProductNode.product.label?.name ?? '',
        description:
          featuredProductNode.product.displayName ||
          featuredProductNode.product.name,
        manufacturer: featuredProductNode.product.provider?.name ?? '',
        manufacturerLink: featuredProductNode.product.manufacturerUrl ?? '',
        productColor: featuredProductNode.color?.name ?? '',
        productColorHex: featuredProductNode.color?.hex ?? '',
        productComposition,
        productSize,
        productTexture: featuredProductNode.texture?.name ?? '',
        isPaintable: featuredProductNode.product.labelId
          ? PAINTABLE_MATERIALS.includes(featuredProductNode.product.labelId)
          : false,
        thumbnailSrc:
          featuredProductNode.texture?.diffuseMapThumbnail?.redirectUrl ?? '',
      });
    }
  }

  return featuredProducts;
};

const InnerFeaturedProducts = ({
  productCatalogVariationUuids,
}: {
  productCatalogVariationUuids: string[];
}) => {
  const { data, loading, error } = useGetProductVariationsQuery({
    variables: { variationIds: productCatalogVariationUuids },
  });

  if (loading || error || !data) {
    if (error) {
      logError(error);
    }
    return null;
  }

  const featuredProducts =
    convertProductCatalogVariationsToFeaturedProducts(data);

  return (
    <FeaturedProductTray
      featuredProducts={featuredProducts}
      testId="featured-products-tray"
    />
  );
};

const FeaturedProducts = ({ img }: FeaturedProductsProps) => {
  const { renderings } = useRenderingContext();
  const renderingFile = findRenderingFile(renderings, img);
  if (!renderingFile) {
    return null;
  }

  const productCatalogVariationUuids: string[] = [];
  const productVariations = renderingFile.productVariations ?? [];
  for (let i = 0; i < productVariations.length; i += 1) {
    if (productVariations[i].productCatalogVariationUuid) {
      productCatalogVariationUuids.push(
        productVariations[i].productCatalogVariationUuid as string,
      );
    }
  }

  if (productCatalogVariationUuids.length === 0) {
    return null;
  }

  return (
    <InnerFeaturedProducts
      productCatalogVariationUuids={productCatalogVariationUuids}
    />
  );
};

export { FeaturedProducts };
